import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Badge,
} from "@mui/material";
import {
  Delete,
  Description,
  Edit,
  FileUpload,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Visibility,
  Home,
  FactCheck,
  AccountBalance,
  Campaign,
  MarkEmailRead,
  ForwardToInbox,
  ConnectWithoutContact,
  AssignmentTurnedIn,
  PictureAsPdf,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import {
  deleteInternalArchive,
  deleteParteDiaria,
  editParteDiaria,
  getInternalArchive,
  getUnreadFileCounts,
  markFileAsRead,
  markParteDiariaRead,
  uploadInternalArchive,
  getAgtComunicados,
  getPartesDiarias,
} from "../services/source";
import dayjs from "dayjs";
import { jsPDF } from "jspdf";
import { openFileUrl } from "../services/fileUtils";
import logo from "../assets/logo.png";
import auth from "../services/auth";
import colors from "../config/colors";

const archiveTabs = [
  {
    key: "administrativo",
    label: "Administrativo",
    icon: <Home fontSize="small" />,
  },
  { key: "controlo", label: "Controlo", icon: <FactCheck fontSize="small" /> },
  { key: "fiscal", label: "Fiscal", icon: <AccountBalance fontSize="small" /> },
  {
    key: "comunicados",
    label: "Comunicados",
    icon: <Campaign fontSize="small" />,
  },
  {
    key: "agt",
    label: "Comunicados AGT",
    icon: <ConnectWithoutContact fontSize="small" />,
  },
  {
    key: "Partes Diárias",
    label: "Partes Diárias",
    icon: <AssignmentTurnedIn fontSize="small" />,
  },
];

const comunicadoTabs = [
  {
    key: "internos",
    label: "Internos",
    icon: <MarkEmailRead fontSize="small" />,
  },
  {
    key: "externos",
    label: "Externos",
    icon: <ForwardToInbox fontSize="small" />,
  },
];

const FILE_BASE_URL = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

const resolveFileUrl = (path) => {
  const cleanedPath = String(path || "").replace(/^\/+/, "");
  if (!cleanedPath) return "#";

  if (/^https?:\/\//i.test(cleanedPath)) {
    return cleanedPath;
  }

  if (FILE_BASE_URL) {
    return `${FILE_BASE_URL}/${cleanedPath}`;
  }

  return `${window.location.origin}/${cleanedPath}`;
};

const normalize = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const generatePartePdf = async (parte) => {
  const doc = new jsPDF({
    unit: "pt",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const marginLeft = 40;
  const maxWidth = pageWidth - marginLeft * 2;
  const lineHeight = 16;

  let y = 25;

  // ==========================
  // HEADER - TITLE LEFT + LOGO RIGHT
  // ==========================
  try {
    const img = await loadImage(logo);

    const logoWidth = 100;
    const logoHeight = (img.height / img.width) * logoWidth;

    // Logo on the right
    const logoX = pageWidth - logoWidth - 20;

    doc.addImage(img, "PNG", logoX, y, logoWidth, logoHeight);

    // Title on the same line
    doc.setFont("helvetica", "bold");

    doc.setFontSize(18);

    doc.text("PARTE DIÁRIA", marginLeft, y + logoHeight / 2 + 6);

    // Move content below header
    y += logoHeight + 20;
  } catch (error) {
    console.warn("Não foi possível carregar o logo do PDF", error);

    doc.setFont("helvetica", "bold");

    doc.setFontSize(18);

    doc.text("PARTE DIÁRIA", marginLeft, y);

    y += 30;
  }

  // ==========================
  // INFORMATION
  // ==========================
  doc.setFontSize(11);

  const fields = [
    {
      label: "Cliente:",
      value: parte.nome_cliente || "-",
    },
    {
      label: "Data:",
      value: parte.parte_created_at
        ? dayjs(parte.parte_created_at).format("DD/MM/YYYY")
        : "-",
    },
    {
      label: "Hora Entrada:",
      value: parte.hora_entrada || "-",
    },
    {
      label: "Hora Saída:",
      value: parte.hora_saida || "-",
    },
    {
      label: "Total:",
      value: parte.horas || "-",
    },
  ];

  fields.forEach(({ label, value }) => {
    doc.setFont("helvetica", "bold");

    doc.text(label, marginLeft, y);

    doc.setFont("helvetica", "normal");

    const split = doc.splitTextToSize(String(value), maxWidth - 100);

    doc.text(split, marginLeft + 100, y);

    y += split.length * lineHeight + 4;
  });

  y += 10;

  // ==========================
  // SERVICE DETAILS
  // ==========================
  const detailFields = [
    {
      label: "Serviço:",
      value: parte.servico || "-",
    },
    {
      label: "Observação:",
      value: parte.observacao || "-",
    },
  ];

  detailFields.forEach(({ label, value }) => {
    doc.setFont("helvetica", "bold");

    doc.text(label, marginLeft, y);

    doc.setFont("helvetica", "normal");

    const split = doc.splitTextToSize(String(value), maxWidth - 100);

    doc.text(split, marginLeft + 100, y);

    y += split.length * lineHeight + 8;
  });

  // ==========================
  // SIGNATURES
  // ==========================
  y += 40;

  const signatureWidth = 180;

  // Technician
  doc.line(marginLeft, y, marginLeft + signatureWidth, y);

  doc.setFontSize(10);

  doc.text("Assinatura Técnico", marginLeft + signatureWidth / 2, y + 15, {
    align: "center",
  });

  // Client
  const clientX = pageWidth - marginLeft - signatureWidth;

  doc.line(clientX, y, clientX + signatureWidth, y);

  doc.text("Assinatura Cliente", clientX + signatureWidth / 2, y + 15, {
    align: "center",
  });

  // ==========================
  // FOOTER
  // ==========================
  doc.setFont("helvetica", "italic");

  doc.setFontSize(8);

  doc.text(
    `Gerado em ${dayjs().format("DD/MM/YYYY HH:mm")}`,
    pageWidth / 2,
    pageHeight - 20,
    {
      align: "center",
    },
  );

  // ==========================
  // OPEN PDF
  // ==========================
  const url = doc.output("bloburl");

  window.open(url, "_blank", "noopener,noreferrer");
};

const formatSize = (size) => {
  if (!size && size !== 0) return "";

  const kilobytes = size / 1024;
  if (kilobytes < 1024) {
    return `${Math.round(kilobytes)} KB`;
  }

  return `${(kilobytes / 1024).toFixed(1)} MB`;
};

const extractComunicadoYear = (comunicado) => {
  const isoCandidate =
    comunicado?.data_referencia || comunicado?.date_iso || "";
  if (isoCandidate) {
    const parsedIso = dayjs(isoCandidate);
    if (parsedIso.isValid()) {
      return String(parsedIso.year());
    }
  }

  const rawDate = String(
    comunicado?.data_texto || comunicado?.date || "",
  ).trim();
  const yearMatch = rawDate.match(/(\d{4})/);
  if (yearMatch?.[1]) {
    return yearMatch[1];
  }

  const updatedAt = comunicado?.updated_at || comunicado?.created_at || "";
  const parsedUpdatedAt = dayjs(updatedAt);
  if (parsedUpdatedAt.isValid()) {
    return String(parsedUpdatedAt.year());
  }

  return null;
};

const InternalArchive = ({ companyId }) => {
  const currentUser = auth.getCurrentUser();
  const userId = currentUser?.data?.id;
  const userType = currentUser?.data?.type;
  const currentYear = String(dayjs().year());
  const [selectedTab, setSelectedTab] = useState("administrativo");
  const [selectedComunicadoTab, setSelectedComunicadoTab] =
    useState("internos");
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [note, setNote] = useState("");
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [fileToDelete, setFileToDelete] = useState(null);
  const [loadingPartesDiarias, setLoadingPartesDiarias] = useState(false);
  const [partesDiarias, setPartesDiarias] = useState([]);
  const [partesError, setPartesError] = useState("");
  const [parteToDelete, setParteToDelete] = useState(null);
  const [parteToEdit, setParteToEdit] = useState(null);
  const [editFormData, setEditFormData] = useState({
    nome_cliente: "",
    hora_entrada: "",
    hora_saida: "",
    horas: "",
    servico: "",
    observacao: "",
  });
  const [editSaving, setEditSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingParte, setDeletingParte] = useState(false);
  const [markingReadId, setMarkingReadId] = useState(null);
  const [openingArchiveId, setOpeningArchiveId] = useState(0);
  const [unreadByCategory, setUnreadByCategory] = useState({});
  const [comunicados, setComunicados] = useState([]);

  const refreshUnreadCount = useCallback(async () => {
    if (!companyId || !userId || !userType) {
      setUnreadByCategory({});
      return;
    }

    const res = await getUnreadFileCounts({
      user_id: userId,
      user_type: userType,
      company_id: companyId,
    });

    if (res?.success) {
      setUnreadByCategory(res?.data?.arquivo_interno_por_categoria || {});
    }
  }, [companyId, userId, userType]);

  const getArchiveTabUnreadCount = (tabKey) => {
    if (tabKey === "administrativo") {
      return Number(unreadByCategory.Administrativo || 0);
    }

    if (tabKey === "controlo") {
      return Number(unreadByCategory.Controlo || 0);
    }

    if (tabKey === "fiscal") {
      return Number(unreadByCategory.Fiscal || 0);
    }

    if (tabKey === "comunicados") {
      return (
        Number(unreadByCategory["Comunicados - Internos"] || 0) +
        Number(unreadByCategory["Comunicados - Externos"] || 0)
      );
    }

    return 0;
  };

  const getComunicadoTabUnreadCount = (tabKey) => {
    if (tabKey === "internos") {
      return Number(unreadByCategory["Comunicados - Internos"] || 0);
    }

    if (tabKey === "externos") {
      return Number(unreadByCategory["Comunicados - Externos"] || 0);
    }

    return 0;
  };

  const selectedLabel = useMemo(() => {
    if (selectedTab === "comunicados") {
      const comunicado = comunicadoTabs.find(
        (item) => item.key === selectedComunicadoTab,
      );
      return `Comunicados - ${comunicado?.label || "Internos"}`;
    }

    return archiveTabs.find((item) => item.key === selectedTab)?.label || "";
  }, [selectedComunicadoTab, selectedTab]);

  const availableYears = useMemo(() => {
    const years = new Set([currentYear]);

    documents.forEach((item) => {
      const date = dayjs(item?.data_registo);
      if (date.isValid()) {
        years.add(String(date.year()));
      }
    });

    comunicados.forEach((item) => {
      const year = extractComunicadoYear(item);
      if (year) {
        years.add(year);
      }
    });

    return [...years].sort((a, b) => Number(b) - Number(a));
  }, [documents, comunicados, currentYear]);

  const filteredDocuments = useMemo(() => {
    const target = normalize(selectedLabel);

    return [...documents]
      .filter((item) => {
        const matchesCategory = normalize(item.categoria) === target;
        if (!matchesCategory) return false;

        const date = dayjs(item?.data_registo);
        const itemYear = date.isValid() ? String(date.year()) : null;
        return itemYear === selectedYear;
      })
      .sort((a, b) => Number(b.id || 0) - Number(a.id || 0));
  }, [documents, selectedLabel, selectedYear]);

  const filteredComunicados = useMemo(() => {
    return [...comunicados]
      .filter((item) => {
        const year = extractComunicadoYear(item);
        return year === selectedYear;
      })
      .sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));
  }, [comunicados, selectedYear]);

  const unreadPartes = useMemo(
    () => partesDiarias.filter((parte) => !parte.lido),
    [partesDiarias],
  );

  const readPartes = useMemo(
    () => partesDiarias.filter((parte) => Boolean(parte.lido)),
    [partesDiarias],
  );

  useEffect(() => {
    let active = true;

    const loadDocuments = async () => {
      if (!companyId) {
        if (active) {
          setDocuments([]);
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);
        const res = await getInternalArchive(companyId);

        if (active) {
          setDocuments(res?.success ? res.data || [] : []);
        }
      } catch (loadError) {
        console.error("Error loading internal archive:", loadError);
        if (active) {
          setDocuments([]);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadDocuments();

    return () => {
      active = false;
    };
  }, [companyId]);

  useEffect(() => {
    refreshUnreadCount();
  }, [refreshUnreadCount]);

  useEffect(() => {
    if (selectedTab === "agt") {
      loadComunicados();
    }
  }, [selectedTab]);

  const loadPartesDiarias = useCallback(async () => {
    if (selectedTab !== "Partes Diárias" || !companyId) {
      setPartesDiarias([]);
      setPartesError("");
      setLoadingPartesDiarias(false);
      return;
    }

    setLoadingPartesDiarias(true);
    setPartesError("");

    try {
      const res = await getPartesDiarias(
        companyId,
        selectedMonth,
        selectedYear,
      );
      if (res?.success) {
        setPartesDiarias(Array.isArray(res.data) ? res.data : []);
      } else {
        setPartesDiarias([]);
        setPartesError(res?.message || "Erro ao carregar partes diárias.");
      }
    } catch (err) {
      console.error("Error loading partes diárias:", err);
      setPartesDiarias([]);
      setPartesError("Erro ao carregar partes diárias.");
    } finally {
      setLoadingPartesDiarias(false);
    }
  }, [companyId, selectedMonth, selectedYear, selectedTab]);

  useEffect(() => {
    loadPartesDiarias();
  }, [loadPartesDiarias]);

  const loadComunicados = async () => {
    try {
      const res = await getAgtComunicados();
      const payload = res?.data || res?.comunicados || [];
      setComunicados(Array.isArray(payload) ? payload : []);
    } catch (err) {
      console.error("Error loading comunicados:", err);
      setComunicados([]);
    }
  };

  const handleTabChange = (_, value) => {
    setSelectedTab(value);
    setError("");
    setSuccess("");
  };

  const handleUpload = async () => {
    if (!companyId) {
      setError("Não foi possível identificar a empresa.");
      return;
    }

    if (!selectedFile) {
      setError("Selecione um ficheiro primeiro.");
      return;
    }

    if (!selectedDate || !dayjs(selectedDate).isValid()) {
      setError("Selecione a data do ficheiro.");
      return;
    }

    setError("");
    setSuccess("");

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("company_id", companyId);
    formData.append("user_id", userId || "");
    formData.append("user_type", userType || "");
    formData.append("categoria", selectedLabel);
    formData.append("data_registo", dayjs(selectedDate).format("YYYY-MM-DD"));
    formData.append("observacoes", note.trim());

    try {
      setUploading(true);
      const res = await uploadInternalArchive(formData);

      if (!res?.data?.success) {
        throw new Error(
          res?.data?.message || "Não foi possível carregar o ficheiro.",
        );
      }

      const uploadedId = Number(res?.data?.id || 0);
      if (!uploadedId) {
        throw new Error("Não foi possível identificar o ficheiro carregado.");
      }

      const refreshed = await getInternalArchive(companyId);
      setDocuments(refreshed?.success ? refreshed.data || [] : []);
      await refreshUnreadCount();
      setSelectedFile(null);
      setSelectedDate(null);
      setNote("");
      setSuccess("Arquivo carregado com sucesso.");
    } catch (uploadError) {
      console.error("Error uploading internal archive file:", uploadError);
      setError(uploadError?.message || "Não foi possível carregar o ficheiro.");
    } finally {
      setUploading(false);
    }
  };

  const handleRequestDelete = (file) => {
    setFileToDelete(file || null);
  };

  const handleCloseDeleteDialog = () => {
    if (deleting) return;
    setFileToDelete(null);
  };

  const handleDelete = async () => {
    if (!fileToDelete?.id) {
      return;
    }

    try {
      setDeleting(true);
      setError("");
      setSuccess("");
      await deleteInternalArchive(fileToDelete.id, companyId);
      const refreshed = await getInternalArchive(companyId);
      setDocuments(refreshed?.success ? refreshed.data || [] : []);
      setSuccess("Arquivo removido com sucesso.");
      setFileToDelete(null);
    } catch (deleteError) {
      console.error("Error deleting internal archive file:", deleteError);
      setError("Não foi possível remover o arquivo.");
    } finally {
      setDeleting(false);
    }
  };

  const handleRequestDeleteParte = (parte) => {
    setParteToDelete(parte || null);
  };

  const handleCloseDeleteParteDialog = () => {
    if (deletingParte) return;
    setParteToDelete(null);
  };

  const handleDeleteParte = async () => {
    if (!parteToDelete?.id) {
      return;
    }

    try {
      setDeletingParte(true);
      setError("");
      setSuccess("");

      const res = await deleteParteDiaria(parteToDelete.id, companyId);
      if (res?.data?.success) {
        setPartesDiarias((prev) =>
          prev.filter((item) => item.id !== parteToDelete.id),
        );
        setSuccess("Parte diária eliminada com sucesso.");
      } else {
        throw new Error(
          res?.data?.message || "Não foi possível eliminar a parte diária.",
        );
      }
    } catch (deleteError) {
      console.error("Error deleting parte diária:", deleteError);
      setError(
        deleteError?.message || "Não foi possível eliminar a parte diária.",
      );
    } finally {
      setDeletingParte(false);
      setParteToDelete(null);
    }
  };

  const handleOpenEditParte = (parte) => {
    setParteToEdit(parte);
    setEditFormData({
      nome_cliente: parte.nome_cliente || "",
      hora_entrada: parte.hora_entrada || "",
      hora_saida: parte.hora_saida || "",
      horas: parte.horas || "",
      servico: parte.servico || "",
      observacao: parte.observacao || "",
    });
  };

  const handleCloseEditParte = () => {
    if (editSaving) return;
    setParteToEdit(null);
  };

  const handleEditFormChange = (field) => (event) => {
    setEditFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSaveEditedParte = async () => {
    if (!parteToEdit?.id || !companyId) {
      return;
    }

    try {
      setEditSaving(true);
      setError("");
      setSuccess("");

      const res = await editParteDiaria({
        parte_id: parteToEdit.id,
        company_id: companyId,
        ...editFormData,
      });

      if (res?.success) {
        setPartesDiarias((prev) =>
          prev.map((item) =>
            item.id === parteToEdit.id ? { ...item, ...editFormData } : item,
          ),
        );
        setSuccess("Parte diária atualizada com sucesso.");
        setParteToEdit(null);
      } else {
        throw new Error(
          res?.message || "Não foi possível atualizar a parte diária.",
        );
      }
    } catch (editError) {
      console.error("Error editing parte diária:", editError);
      setError(
        editError?.message || "Não foi possível atualizar a parte diária.",
      );
    } finally {
      setEditSaving(false);
    }
  };

  const handleMarkParteAsRead = async (parte) => {
    if (!parte?.id || parte.lido || !companyId) {
      return;
    }

    try {
      setMarkingReadId(parte.id);
      setError("");
      setSuccess("");

      const res = await markParteDiariaRead(parte.id, companyId);
      if (res?.success) {
        setPartesDiarias((prev) =>
          prev.map((item) =>
            item.id === parte.id ? { ...item, lido: 1 } : item,
          ),
        );
        setSuccess("Parte diária marcada como lida.");
      } else {
        throw new Error(res?.message || "Não foi possível marcar como lida.");
      }
    } catch (readError) {
      console.error("Error marking parte diária as read:", readError);
      setError(readError?.message || "Não foi possível marcar como lida.");
    } finally {
      setMarkingReadId(null);
    }
  };

  const renderParteAccordion = (parte) => (
    <Accordion key={parte.id} sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<KeyboardArrowDown />}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography fontWeight={700} noWrap>
              {parte.nome_cliente || "Sem cliente"}
            </Typography>
            <Chip
              label={parte.lido ? "Lido" : "Não lido"}
              size="small"
              color={parte.lido ? "success" : "warning"}
            />
          </Stack>
          <Chip
            label={dayjs(parte.parte_created_at).format("DD/MM/YYYY")}
            size="small"
            color="secondary"
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Typography>
          <strong>Hora Entrada:</strong> {parte.hora_entrada}
        </Typography>
        <Typography>
          <strong>Hora Saída:</strong> {parte.hora_saida}
        </Typography>
        <Typography>
          <strong>Total:</strong> {parte.horas}
        </Typography>
        <Typography sx={{ mt: 1 }}>
          <strong>Serviço:</strong> {parte.servico}
        </Typography>
        {parte.observacao ? (
          <Typography sx={{ mt: 1 }}>
            <strong>Observação:</strong> {parte.observacao}
          </Typography>
        ) : null}

        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 1 }} />

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              startIcon={<PictureAsPdf />}
              onClick={() => generatePartePdf(parte)}
              size="small"
              variant="outlined"
              sx={{
                textTransform: "none",
                borderRadius: 3,
                color: "#7b1fa2",
                borderColor: "#7b1fa2",
              }}
            >
              PDF
            </Button>
            {parte.pdf_caminho ? (
              <Button
                onClick={async () => {
                  try {
                    await openFileUrl(parte.pdf_caminho, parte.pdf_nome);
                  } finally {
                    handleMarkParteAsRead(parte);
                  }
                }}
                size="small"
                variant="contained"
                sx={{
                  textTransform: "none",
                  borderRadius: 3,
                  backgroundColor: "#7b1fa2",
                  borderColor: "#7b1fa2",
                }}
              >
                Abrir PDF existente
              </Button>
            ) : null}
            <Button
              startIcon={<AssignmentTurnedIn />}
              onClick={() => handleMarkParteAsRead(parte)}
              size="small"
              variant="outlined"
              disabled={parte.lido || markingReadId === parte.id}
              sx={{
                textTransform: "none",
                borderRadius: 3,
              }}
            >
              {parte.lido ? "Lido" : "Marcar como lido"}
            </Button>
            <Button
              startIcon={<Edit />}
              onClick={() => handleOpenEditParte(parte)}
              size="small"
              variant="outlined"
              sx={{
                textTransform: "none",
                borderRadius: 3,
              }}
            >
              Editar
            </Button>
            <Button
              startIcon={<Delete />}
              onClick={() => handleRequestDeleteParte(parte)}
              size="small"
              variant="outlined"
              color="error"
              sx={{
                textTransform: "none",
                borderRadius: 3,
              }}
            >
              Eliminar
            </Button>
          </Stack>
        </Box>

        {parte.assinatura_tecnico || parte.assinatura_cliente ? (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 1 }} />
            <Typography fontWeight={700} mb={1}>
              Assinaturas
            </Typography>
            {parte.assinatura_tecnico ? (
              <Typography>
                <strong>Técnico:</strong> {parte.assinatura_tecnico}
              </Typography>
            ) : null}
            {parte.assinatura_cliente ? (
              <Typography>
                <strong>Cliente:</strong> {parte.assinatura_cliente}
              </Typography>
            ) : null}
          </Box>
        ) : null}

        {parte.fotos?.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 1 }} />
            <Typography fontWeight={700} mb={1}>
              Fotos
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {parte.fotos.map((foto) => (
                <Chip
                  key={foto.id}
                  label={foto.nome_original}
                  clickable
                  onClick={async () => {
                    try {
                      await openFileUrl(foto.caminho, foto.nome_ficheiro);
                    } finally {
                      handleMarkParteAsRead(parte);
                    }
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );

  const handleOpenArchiveFile = async (file) => {
    if (!file?.caminho) {
      return;
    }

    setOpeningArchiveId(Number(file.id));
    try {
      const ok = await openFileUrl(
        file.caminho,
        file.nome_original || file.nome_ficheiro,
      );
      if (!ok) {
        window.open(
          resolveFileUrl(file.caminho),
          "_blank",
          "noopener,noreferrer",
        );
      }
    } catch (err) {
      // fallback to direct open
      window.open(
        resolveFileUrl(file.caminho),
        "_blank",
        "noopener,noreferrer",
      );
    } finally {
      setOpeningArchiveId(0);
    }

    if (!userId || !userType || !file?.id) {
      return;
    }

    await markFileAsRead({
      user_id: userId,
      user_type: userType,
      file_type: "internal_archive",
      file_id: Number(file.id),
    });

    await refreshUnreadCount();
  };

  return (
    <Card
      sx={{
        borderRadius: 4,
        border: "1px solid #e7e9f1",
        background:
          "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,249,253,1) 100%)",
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Arquivo Interno da Empresa
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Organize documentos da empresa por área e carregue ficheiros.
            </Typography>
          </Box>

          <Chip
            label={`${selectedTab === "agt" ? filteredComunicados.length : filteredDocuments.length} arquivo(s)`}
            color="secondary"
            sx={{ fontWeight: 700 }}
          />
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ mb: 2 }}
        >
          <TextField
            select
            label="Ano"
            size="small"
            value={selectedYear}
            onChange={(event) => setSelectedYear(event.target.value)}
            sx={{ minWidth: { xs: "100%", sm: 160 } }}
          >
            {availableYears.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </TextField>
          {selectedTab === "Partes Diárias" ? (
            <TextField
              select
              label="Mês"
              size="small"
              value={selectedMonth}
              onChange={(event) => setSelectedMonth(event.target.value)}
              sx={{ minWidth: { xs: "100%", sm: 160 } }}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="1">Janeiro</MenuItem>
              <MenuItem value="2">Fevereiro</MenuItem>
              <MenuItem value="3">Março</MenuItem>
              <MenuItem value="4">Abril</MenuItem>
              <MenuItem value="5">Maio</MenuItem>
              <MenuItem value="6">Junho</MenuItem>
              <MenuItem value="7">Julho</MenuItem>
              <MenuItem value="8">Agosto</MenuItem>
              <MenuItem value="9">Setembro</MenuItem>
              <MenuItem value="10">Outubro</MenuItem>
              <MenuItem value="11">Novembro</MenuItem>
              <MenuItem value="12">Dezembro</MenuItem>
            </TextField>
          ) : null}
          <Typography variant="caption" color="text.secondary">
            {selectedTab === "Partes Diárias"
              ? "Filtro por ano e mês"
              : "Filtro global por ano"}
          </Typography>
        </Stack>

        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            mb: 2,
            backgroundColor: "rgba(114, 4, 241, 0.12)",
            borderRadius: 2,
            minHeight: 34,
            "& .MuiTabs-indicator": { display: "none" },
          }}
        >
          {archiveTabs.map((item) => (
            <Tab
              key={item.key}
              value={item.key}
              icon={item.icon}
              iconPosition="start"
              label={
                getArchiveTabUnreadCount(item.key) > 0 ? (
                  <Badge
                    color="error"
                    badgeContent={getArchiveTabUnreadCount(item.key)}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    sx={{
                      "& .MuiBadge-badge": {
                        minWidth: 14,
                        height: 14,
                        fontSize: "0.65rem",
                        px: 0.5,
                        top: 2,
                        right: -8,
                      },
                    }}
                  >
                    <span>{item.label}</span>
                  </Badge>
                ) : (
                  item.label
                )
              }
              sx={{
                textTransform: "none",
                minHeight: 44,
                py: 0.35,
                px: 1.1,
                "&.Mui-selected": {
                  color: "#fff",
                  backgroundColor: "#9c27b0",
                },
              }}
            />
          ))}
        </Tabs>

        {selectedTab === "comunicados" && (
          <Tabs
            value={selectedComunicadoTab}
            onChange={(_, value) => setSelectedComunicadoTab(value)}
            variant="fullWidth"
            sx={{
              mb: 2,
              borderRadius: 2,
              backgroundColor: "#f3f5fb",
              minHeight: 32,
              "& .MuiTabs-indicator": { display: "none" },
            }}
          >
            {comunicadoTabs.map((item) => (
              <Tab
                key={item.key}
                value={item.key}
                icon={item.icon}
                iconPosition="start"
                label={
                  getComunicadoTabUnreadCount(item.key) > 0 ? (
                    <Badge
                      color="error"
                      badgeContent={getComunicadoTabUnreadCount(item.key)}
                      anchorOrigin={{ vertical: "top", horizontal: "right" }}
                      sx={{
                        "& .MuiBadge-badge": {
                          minWidth: 14,
                          height: 14,
                          fontSize: "0.65rem",
                          px: 0.5,
                          top: 2,
                          right: -8,
                        },
                      }}
                    >
                      <span>{item.label}</span>
                    </Badge>
                  ) : (
                    item.label
                  )
                }
                sx={{
                  textTransform: "none",
                  minHeight: 32,
                  py: 0.25,
                  px: 1,
                  "&.Mui-selected": {
                    color: "#5b2c72",
                    backgroundColor: "rgba(91,44,114,0.08)",
                  },
                }}
              />
            ))}
          </Tabs>
        )}

        {selectedTab !== "agt" && selectedTab !== "Partes Diárias" && (
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 1.5, sm: 2 },
              borderRadius: 3,
              borderColor: "#e4e7ef",
              mb: 2,
            }}
          >
            <Stack spacing={1.5}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  Novo arquivo
                </Typography>
                <Tooltip
                  title={
                    showUploadForm ? "Ocultar formulário" : "Mostrar formulário"
                  }
                >
                  <IconButton
                    size="small"
                    onClick={() => setShowUploadForm((prev) => !prev)}
                    aria-label={
                      showUploadForm
                        ? "Ocultar formulário"
                        : "Mostrar formulário"
                    }
                  >
                    {showUploadForm ? (
                      <KeyboardArrowUp />
                    ) : (
                      <KeyboardArrowDown />
                    )}
                  </IconButton>
                </Tooltip>
              </Stack>

              {showUploadForm ? (
                <>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", sm: "center" }}
                  >
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<FileUpload />}
                      sx={{
                        textTransform: "none",
                        width: { xs: "100%", sm: "auto" },
                      }}
                    >
                      Selecionar ficheiro
                      <input
                        hidden
                        type="file"
                        onChange={(event) => {
                          const file = event.target.files?.[0] || null;
                          setSelectedFile(file);
                          if (file) {
                            setError("");
                            setSuccess("");
                          }
                        }}
                      />
                    </Button>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        textAlign: { xs: "left", sm: "center" },
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {selectedFile?.name || "Nenhum ficheiro selecionado"}
                    </Typography>

                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleUpload}
                      disabled={uploading || !selectedFile || !selectedDate}
                      sx={{
                        textTransform: "none",
                        width: { xs: "100%", sm: "auto" },
                      }}
                    >
                      {uploading ? "A carregar..." : "Carregar arquivo"}
                    </Button>
                  </Stack>
                  <DatePicker
                    label="Data"
                    value={selectedDate}
                    onChange={(newValue) => setSelectedDate(newValue)}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                      },
                    }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Observações"
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    multiline
                    minRows={2}
                  />
                </>
              ) : null}

              <Typography variant="caption" color="text.secondary">
                Categoria ativa: {selectedLabel}
              </Typography>
            </Stack>
          </Paper>
        )}

        {selectedTab === "Partes Diárias" && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Este separador mostra as partes diárias registadas para a empresa.
            </Alert>
            {partesError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {partesError}
              </Alert>
            ) : null}
            {loadingPartesDiarias ? (
              <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
                <CircularProgress size={26} />
              </Box>
            ) : partesDiarias.length ? (
              <Box sx={{ mb: 2 }}>
                {unreadPartes.length ? (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Não lidos
                    </Typography>
                  </Box>
                ) : null}
                {unreadPartes.map(renderParteAccordion)}
                {readPartes.length ? (
                  <Box sx={{ mb: 2, mt: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Lidos
                    </Typography>
                  </Box>
                ) : null}
                {readPartes.map(renderParteAccordion)}
              </Box>
            ) : (
              <Box
                sx={{
                  py: 4,
                  textAlign: "center",
                  color: "text.secondary",
                }}
              >
                <Typography variant="body2">
                  Nenhuma parte diária encontrada para esta empresa.
                </Typography>
              </Box>
            )}
          </>
        )}

        <Dialog
          open={Boolean(parteToDelete)}
          onClose={handleCloseDeleteParteDialog}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Eliminar parte diária</DialogTitle>
          <DialogContent>
            <Typography>
              Tem certeza que deseja eliminar a parte diária de{" "}
              {parteToDelete?.nome_cliente || "este registro"}?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseDeleteParteDialog}
              disabled={deletingParte}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteParte}
              color="error"
              disabled={deletingParte}
            >
              {deletingParte ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(parteToEdit)}
          onClose={handleCloseEditParte}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Editar parte diária</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <TextField
                label="Cliente"
                value={editFormData.nome_cliente}
                onChange={handleEditFormChange("nome_cliente")}
                fullWidth
                size="small"
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField
                  label="Hora Entrada"
                  value={editFormData.hora_entrada}
                  onChange={handleEditFormChange("hora_entrada")}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Hora Saída"
                  value={editFormData.hora_saida}
                  onChange={handleEditFormChange("hora_saida")}
                  fullWidth
                  size="small"
                />
              </Stack>
              <TextField
                label="Horas"
                value={editFormData.horas}
                onChange={handleEditFormChange("horas")}
                fullWidth
                size="small"
              />
              <TextField
                label="Serviço"
                value={editFormData.servico}
                onChange={handleEditFormChange("servico")}
                fullWidth
                size="small"
                multiline
                minRows={2}
              />
              <TextField
                label="Observação"
                value={editFormData.observacao}
                onChange={handleEditFormChange("observacao")}
                fullWidth
                size="small"
                multiline
                minRows={2}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditParte} disabled={editSaving}>
              Cancelar
            </Button>
            <Button
              onClick={handleSaveEditedParte}
              variant="contained"
              disabled={editSaving}
              sx={{
                borderRadius: 5,
                textTransform: "none",
                backgroundColor: colors.secondary,
              }}
            >
              {editSaving ? "A guardar..." : "Guardar"}
            </Button>
          </DialogActions>
        </Dialog>

        {selectedTab !== "agt" && selectedTab !== "Partes Diárias" && (
          <Divider sx={{ mb: 2 }} />
        )}

        {loading ? (
          <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={26} />
          </Box>
        ) : selectedTab === "agt" ? (
          filteredComunicados.length ? (
            <Stack spacing={2}>
              {filteredComunicados.map((item) => {
                const dateText = dayjs(item.data_texto).isValid()
                  ? dayjs(item.data_texto).format("DD/MM/YYYY")
                  : item.data_texto ||
                    item.data_referencia ||
                    "Data não disponível";

                return (
                  <Card
                    key={item.id || item.url || dateText}
                    variant="outlined"
                    elevation={3}
                  >
                    <CardContent>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        spacing={1}
                      >
                        <Typography variant="h6" fontWeight={700}>
                          {item.comunicado || "Comunicado AGT"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {dateText}
                        </Typography>
                      </Stack>

                      {item.comunicado_texto ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1, whiteSpace: "pre-line" }}
                        >
                          {item.comunicado_texto}
                        </Typography>
                      ) : null}
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          ) : (
            <Box
              sx={{
                py: 4,
                textAlign: "center",
                color: "text.secondary",
              }}
            >
              <Typography variant="body2">
                Sem comunicados AGT para o ano selecionado.
              </Typography>
            </Box>
          )
        ) : filteredDocuments.length ? (
          <List dense disablePadding>
            {filteredDocuments.map((file) => (
              <ListItem
                key={file.id}
                divider
                sx={{ alignItems: "flex-start", py: 1.25 }}
              >
                <ListItemAvatar>
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: 2,
                      bgcolor: "#f3e8fb",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Description sx={{ color: "#7b1fa2" }} />
                  </Box>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    file.nome_original || file.nome_ficheiro || "Arquivo"
                  }
                  secondary={
                    <Stack spacing={0.4} sx={{ mt: 0.4 }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatSize(file.tamanho)}
                      </Typography>
                      {file.data_registo ? (
                        <Typography variant="caption" color="text.secondary">
                          {`Data: ${file.data_registo}`}
                        </Typography>
                      ) : null}
                      {file.observacoes ? (
                        <Typography variant="caption" color="text.secondary">
                          {file.observacoes}
                        </Typography>
                      ) : null}
                    </Stack>
                  }
                  primaryTypographyProps={{ fontWeight: 600 }}
                  secondaryTypographyProps={{ component: "div" }}
                />
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{ ml: 1, alignSelf: { xs: "center", sm: "flex-start" } }}
                >
                  <Tooltip title="Abrir ficheiro em nova aba">
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenArchiveFile(file)}
                      aria-label="Abrir ficheiro em nova aba"
                      disabled={openingArchiveId === Number(file.id)}
                    >
                      {openingArchiveId === Number(file.id) ? (
                        <CircularProgress size={18} />
                      ) : (
                        <Visibility sx={{ color: "#ed6c02" }} />
                      )}
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    color="error"
                    onClick={() => handleRequestDelete(file)}
                  >
                    <Delete />
                  </IconButton>
                </Stack>
              </ListItem>
            ))}
          </List>
        ) : (
          <Box
            sx={{
              py: 4,
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            <Typography variant="body2">
              Sem ficheiros nesta categoria.
            </Typography>
          </Box>
        )}

        <Dialog
          open={Boolean(fileToDelete)}
          onClose={handleCloseDeleteDialog}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Confirmar eliminação</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              Tem a certeza que deseja eliminar este arquivo?
            </Typography>
            <Typography sx={{ mt: 1 }} fontWeight={600}>
              {fileToDelete?.nome_original ||
                fileToDelete?.nome_ficheiro ||
                "Arquivo"}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog} disabled={deleting}>
              Cancelar
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? "A eliminar..." : "Eliminar"}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default InternalArchive;
